import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Mail, Phone, MapPin, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { SiteSettings } from '../../types';
import { apiService } from '../../services/api';

interface Policy {
  id: number;
  title: string;
  slug: string;
  policy_type: string;
  is_active: boolean;
}

export const Footer: React.FC = () => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);

  useEffect(() => {
    fetchSiteSettings();
    fetchPolicies();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await apiService.getSiteSettings();
      if (response.success) {
        setSiteSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await apiService.getAllPolicies();
      if (response.success && response.data) {
        setPolicies(response.data);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  };
  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo 1 (6).png" 
                alt="AARAN HOMES Logo" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <h3 className="text-lg font-bold">{siteSettings?.site_name || 'AARAN HOMES'}</h3>
                <p className="text-sm text-gray-400">{siteSettings?.site_description || 'Your Dream Home Awaits'}</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Your trusted partner in finding the perfect property. We offer a comprehensive 
              range of residential, commercial, retail, and industrial properties to meet 
              all your real estate needs.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/people/AARAN-HOMES/100071075424040/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-200"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/aaran_homes/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-primary transition-colors duration-200"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={`https://wa.me/${(siteSettings?.contact_phone || '+919867300996').replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-green-500 transition-colors duration-200"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Policies */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Policies</h3>
            <ul className="space-y-2">
              {policies.length > 0 ? (
                policies.map((policy) => (
                  <li key={policy.id}>
                    <Link
                      to={`/policy/${policy.slug}`}
                      className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                    >
                      {policy.title}
                    </Link>
                  </li>
                ))
              ) : (
                <>
                  <li>
                    <Link
                      to="/policy/terms_conditions"
                      className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/policy/refund_policy"
                      className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                    >
                      Refund Policy
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {siteSettings?.contact_address ||
                      "345, 3rd FLOOR, IJMIMA COMPLEX BEHIND INFINITY MALL, MINDSPACE , MALAD(WEST), MUMBAI-400064."
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0" />
                <div>
                  <a
                    href={`tel:${siteSettings?.contact_phone || '+919867300996'}`}
                    className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                    title={`Call us: ${siteSettings?.contact_phone || '+919867300996'}`}
                  >
                    {siteSettings?.contact_phone || '+919867300996'}
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                <div>
                  <a
                    href={`mailto:${siteSettings?.contact_email || 'info@aaranhomes.com'}`}
                    className="text-gray-300 hover:text-secondary transition-colors duration-200 text-sm"
                    title={`Email us: ${siteSettings?.contact_email || 'info@aaranhomes.com'}`}
                  >
                    {siteSettings?.contact_email || 'info@aaranhomes.com'}
                  </a>
                </div>
              </div>
            </div>

            
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-center items-center">
            <div className="text-gray-400 text-sm">
              © 2024 AARAN HOMES. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};